// services/orderService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 15000
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('kapta_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isPublic = url.includes('/track');

      if (!isPublic) {
        localStorage.removeItem('kapta_token');
        localStorage.removeItem('kapta_user');
        const path = window.location.pathname;
        if (path.startsWith('/user/')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

/* ── Small helper to standardise error messages ── */
const wrap = async (promise, fallback = 'Request failed') => {
  try {
    const response = await promise;
    return response.data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      fallback;
    const wrapped = new Error(message);
    wrapped.response = error.response;
    throw wrapped;
  }
};

export const orderService = {
  /* ═══════════════════════════════════════════════════════════════
     USER ORDERS (Auth required)
     ═══════════════════════════════════════════════════════════════ */

  /**
   * List all orders belonging to the logged-in user.
   * Feeds the All Orders page.
   *
   * @param {Object} [params]
   * @param {string} [params.status]         - orderStatus filter (e.g. 'delivered')
   * @param {string} [params.paymentStatus]  - 'pending' | 'paid' | 'failed' | 'refunded'
   * @param {number} [params.page=1]
   * @param {number} [params.limit=20]
   * @param {'newest'|'oldest'} [params.sort='newest']
   *
   * Returns: { success, data: [...orders], pagination: { page, limit, total, pages } }
   */
  async getMyOrders(params = {}) {
    const { status, paymentStatus, page = 1, limit = 20, sort = 'newest' } =
      params;

    const query = new URLSearchParams();
    if (status) query.append('status', status);
    if (paymentStatus) query.append('paymentStatus', paymentStatus);
    query.append('page', page);
    query.append('limit', limit);
    query.append('sort', sort);

    return wrap(
      api.get(`/api/v1/orders?${query.toString()}`),
      'Failed to get orders'
    );
  },

  /**
   * Alias kept for readability where `getAllOrders` reads better.
   */
  async getAllOrders(params = {}) {
    return this.getMyOrders(params);
  },

  /**
   * Get a single order by ID (owner only).
   * Returns: { success, data: {...order} }
   */
  async getOrderById(orderId) {
    return wrap(
      api.get(`/api/v1/orders/${orderId}`),
      'Failed to get order'
    );
  },

  /**
   * Cancel an order. Only allowed while orderStatus is 'pending' or 'processing'.
   * Returns: { success, message, data: { id, orderNumber, orderStatus } }
   */
  async cancelOrder(orderId, reason = '') {
    return wrap(
      api.patch(`/api/v1/orders/${orderId}/cancel`, { reason }),
      'Failed to cancel order'
    );
  }
};

export default orderService;